import { request } from "./api";
export type Member = {
  user_id: string;
  email: string;
  role: "owner" | "developer" | "viewer";
};
export type Variable = {
  id: string;
  service_id: string;
  environment: "production" | "preview";
  key: string;
  is_secret: boolean;
  build_time: boolean;
};
export type PreviewPolicy = {
  enabled: boolean;
  ttl_hours: number;
  max_active: number;
};
export type PreviewGroup = {
  service_id: string;
  name: string;
  policy: PreviewPolicy;
  previews: {
    pr: number;
    deployment_id: string;
    status: string;
    url: string;
    commit_sha: string;
    created_at: string;
    expires_at: string;
    removed_at: string | null;
  }[];
};
export type CommandCenter = {
  observed_at: string;
  events: {
    at: string;
    deployment_id: string;
    service_id: string;
    stage: string;
    error: string | null;
  }[];
  issues: {
    deployment_id: string;
    service_id: string;
    error: string | null;
    category: string;
    action: string;
    basis: string;
  }[];
  dependencies: {
    id: string;
    name: string;
    status: string;
    error: string | null;
  }[];
};
export type Protection = {
  id: string;
  name: string;
  engine: string;
  plan: string;
  status: string;
  protection: string;
  policy: { enabled: boolean; interval_hours: number; retention_days: number };
  last_success_at: string | null;
  next_due_at: string | null;
  backup_bytes: number;
  allocated_storage_gb: number;
  latest_backup_id: string | null;
  latest_error: string | null;
};
export type Restore = {
  id: string;
  backup_id: string;
  target_id: string;
  status: string;
  error: string | null;
  created_at: string;
  finished_at: string | null;
};
export type Sample = {
  at: string;
  cpu_millicores: number;
  memory_bytes: number;
  samples: number;
};
export type Insights = {
  currency: string;
  charged: string;
  refunds: string;
  configured_monthly: string;
  wallet_balance: string;
  observed_at: string;
  charged_etb: string;
  refunds_etb: string;
  net_spend_etb: string;
  configured_monthly_etb: string;
  wallet_balance_etb: string;
  budget: {
    monthly_amount: string;
    monthly_etb: string;
    warning_percent: number;
    used_percent: number;
    state: string;
  } | null;
  daily_spend: { day: string; net_etb: string; net: string }[];
  resources: {
    id: string;
    name: string;
    kind: string;
    status: string;
    monthly_etb: string;
    monthly_price: string;
    allocated_storage_gb: number;
    cpu_millicores: number | null;
    memory_bytes: number | null;
    sample_at: string | null;
    database_health?: { restarts?: number; disk_used_percent?: number; connections?: number; in_recovery?: boolean } | null;
    history: Sample[];
  }[];
};
export type Template = {
  id: string;
  name: string;
  description: string;
  version: number;
  components: number;
  databases: number;
  monthly_etb: string;
};
export type ConfiguredTemplate = {
  filename: string;
  content: string;
  steps: string[];
  notes: string[];
  version: number;
};
const w = (id: string) => `/v1/workspaces/${encodeURIComponent(id)}`;
const json = (method: string, body?: unknown) => ({
  method,
  body: body === undefined ? undefined : JSON.stringify(body),
});
export const workspace = {
  command: (id: string) => request<CommandCenter>(`${w(id)}/command-center`),
  members: (id: string) => request<Member[]>(`${w(id)}/members`),
  activity: (id: string) =>
    request<{ id: number; at: string; actor: string; action: string }[]>(
      `${w(id)}/activity`,
    ),
  invite: (id: string, email: string, role: string) =>
    request<{ id: string; token: string; expires_at: string }>(
      `${w(id)}/invitations`,
      json("POST", { email, role }),
    ),
  invitations: (id: string) =>
    request<{ id: string; email: string; role: string; expires_at: string }[]>(
      `${w(id)}/invitations`,
    ),
  revoke: (id: string, invite: string) =>
    request<void>(`${w(id)}/invitations/${invite}`, json("DELETE")),
  accept: (token: string) =>
    request<{ project_id: string }>(
      "/v1/workspaces/invitations/accept",
      json("POST", { token }),
    ),
  role: (id: string, member: string, role: string) =>
    request(`${w(id)}/members/${member}`, json("PATCH", { role })),
  remove: (id: string, member: string) =>
    request(`${w(id)}/members/${member}`, json("DELETE")),
  variables: (id: string) => request<Variable[]>(`${w(id)}/variables`),
  saveVariable: (id: string, value: Omit<Variable, "id"> & { value: string }) =>
    request(`${w(id)}/variables`, json("PUT", value)),
  deleteVariable: (id: string, variable: string) =>
    request(`${w(id)}/variables/${variable}`, json("DELETE")),
  previews: (id: string) => request<PreviewGroup[]>(`${w(id)}/previews`),
  savePreview: (id: string, service: string, policy: PreviewPolicy) =>
    request(`${w(id)}/previews/${service}/policy`, json("PUT", policy)),
  removePreview: (id: string, service: string, pr: number) =>
    request(`${w(id)}/previews/${service}/${pr}`, json("DELETE")),
  protection: () => request<Protection[]>("/v1/protection"),
  restores: () => request<Restore[]>("/v1/protection/restores"),
  saveProtection: (id: string, policy: Protection["policy"]) =>
    request(`/v1/protection/${id}/policy`, json("PUT", policy)),
  restoreCopy: (id: string, backup: string, name: string) =>
    request<{ id: string; target_id: string }>(
      `/v1/protection/${id}/backups/${backup}/restore-copy`,
      json("POST", { name }),
    ),
  insights: () => request<Insights>("/v1/insights"),
  budget: (monthly_etb: string, warning_percent: number, currency = "ETB") =>
    request(
      "/v1/insights/budget",
      json("PUT", { monthly_etb, warning_percent, currency }),
    ),
  removeBudget: (currency = "ETB") => request(`/v1/insights/budget?currency=${encodeURIComponent(currency)}`, json("DELETE")),
  templates: () => request<Template[]>("/v1/templates"),
  configure: (id: string, name: string) =>
    request<ConfiguredTemplate>(
      `/v1/templates/${id}/configure`,
      json("POST", { name }),
    ),
};
